import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Plus, Trash2, Edit2, GripVertical, Star, Check, X, ChevronDown, ChevronRight } from 'lucide-react'
import { toast } from 'sonner'
import { pipelineApi } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { Pipeline, PipelineStage, CreatePipelineRequest, CreatePipelineStageRequest } from '@/types'
import type { TFunction } from 'i18next'

const DEFAULT_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16']

// Pipeline Card Component
interface PipelineCardProps {
  pipeline: Pipeline
  t: TFunction
  onEdit: (pipeline: Pipeline) => void
  onDelete: (pipeline: Pipeline) => void
  onSetDefault: (id: string) => void
  onAddStage: (pipelineId: string) => void
  onEditStage: (pipelineId: string, stage: PipelineStage) => void
  onDeleteStage: (pipelineId: string, stage: PipelineStage) => void
}

function PipelineCard({ pipeline, t, onEdit, onDelete, onSetDefault, onAddStage, onEditStage, onDeleteStage }: PipelineCardProps) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="border rounded-lg bg-card">
      <CollapsibleTrigger asChild>
        <button type="button" className="flex items-center justify-between w-full px-4 py-3 hover:bg-muted/50 text-left">
          <div className="flex items-center gap-3">
            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <span className="font-medium">{pipeline.pipeline_name}</span>
            {pipeline.is_default && (
              <Badge variant="secondary" className="gap-1">
                <Star className="h-3 w-3" />
                {t('common.default', 'Default')}
              </Badge>
            )}
            {!pipeline.is_active && <Badge variant="outline">{t('common.inactive', 'Inactive')}</Badge>}
          </div>
          <span className="text-sm text-muted-foreground">
            {pipeline.stages?.length || 0} {t('crm.stages', 'stages')}
          </span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="px-4 pb-4">
        <div className="flex items-center justify-between mb-4 pt-2">
          <p className="text-sm text-muted-foreground">{pipeline.pipeline_description || t('crm.noDescription', 'No description')}</p>
          <div className="flex gap-2">
            {!pipeline.is_default && (
              <Button variant="outline" size="sm" onClick={() => onSetDefault(pipeline.pipeline_id)}>
                <Star className="h-4 w-4 mr-1" />
                {t('crm.setDefault', 'Set as Default')}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => onEdit(pipeline)}>
              <Edit2 className="h-4 w-4 mr-1" />
              {t('common.edit', 'Edit')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => onDelete(pipeline)} disabled={pipeline.is_default}>
              <Trash2 className="h-4 w-4 mr-1" />
              {t('common.delete', 'Delete')}
            </Button>
          </div>
        </div>

        {/* Stages Table */}
        <Card>
          <CardHeader className="py-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">{t('crm.stages', 'Stages')}</CardTitle>
              <Button size="sm" onClick={() => onAddStage(pipeline.pipeline_id)}>
                <Plus className="h-4 w-4 mr-1" />
                {t('crm.addStage', 'Add Stage')}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>{t('crm.stageName', 'Stage Name')}</TableHead>
                  <TableHead>{t('crm.color', 'Color')}</TableHead>
                  <TableHead>{t('crm.probability', 'Probability')}</TableHead>
                  <TableHead>{t('crm.type', 'Type')}</TableHead>
                  <TableHead>{t('common.status', 'Status')}</TableHead>
                  <TableHead className="text-right">{t('common.actions', 'Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(pipeline.stages || []).sort((a, b) => a.display_order - b.display_order).map((stage) => (
                  <TableRow key={stage.stage_id}>
                    <TableCell><GripVertical className="h-4 w-4 text-muted-foreground" /></TableCell>
                    <TableCell className="font-medium">{stage.stage_name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: stage.stage_color }} />
                        <span className="text-sm text-muted-foreground">{stage.stage_color}</span>
                      </div>
                    </TableCell>
                    <TableCell>{stage.probability}%</TableCell>
                    <TableCell>
                      {stage.is_won_stage && <Badge className="bg-green-500"><Check className="h-3 w-3 mr-1" />{t('crm.won', 'Won')}</Badge>}
                      {stage.is_lost_stage && <Badge variant="destructive"><X className="h-3 w-3 mr-1" />{t('crm.lost', 'Lost')}</Badge>}
                      {!stage.is_won_stage && !stage.is_lost_stage && <span className="text-muted-foreground">-</span>}
                    </TableCell>
                    <TableCell>
                      <Badge variant={stage.is_active ? 'default' : 'secondary'}>
                        {stage.is_active ? t('common.active', 'Active') : t('common.inactive', 'Inactive')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => onEditStage(pipeline.pipeline_id, stage)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => onDeleteStage(pipeline.pipeline_id, stage)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {(!pipeline.stages || pipeline.stages.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      {t('crm.noStages', 'No stages configured. Add stages to start using this pipeline.')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </CollapsibleContent>
    </Collapsible>
  )
}

export default function PipelineSettingsPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  // Pipeline state
  const [pipelineDialogOpen, setPipelineDialogOpen] = useState(false)
  const [editingPipeline, setEditingPipeline] = useState<Pipeline | null>(null)
  const [pipelineForm, setPipelineForm] = useState<CreatePipelineRequest>({ pipeline_name: '', pipeline_description: '' })

  // Stage state
  const [stageDialogOpen, setStageDialogOpen] = useState(false)
  const [editingStage, setEditingStage] = useState<PipelineStage | null>(null)
  const [selectedPipelineId, setSelectedPipelineId] = useState<string | null>(null)
  const [stageForm, setStageForm] = useState<CreatePipelineStageRequest>({ stage_name: '', stage_color: '#3B82F6', probability: 0 })

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<{ type: 'pipeline' | 'stage'; id: string; name: string; pipelineId?: string } | null>(null)

  // Queries
  const { data: pipelines = [], isLoading } = useQuery({
    queryKey: ['pipelines-all'],
    queryFn: () => pipelineApi.getPipelines(false),
  })

  // Pipeline mutations
  const createPipelineMutation = useMutation({
    mutationFn: (data: CreatePipelineRequest) => pipelineApi.createPipeline(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipelines-all'] })
      queryClient.invalidateQueries({ queryKey: ['pipelines'] })
      toast.success(t('messages.createSuccess'))
      setPipelineDialogOpen(false)
      setPipelineForm({ pipeline_name: '', pipeline_description: '' })
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('messages.createError'))),
  })

  const updatePipelineMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreatePipelineRequest }) => pipelineApi.updatePipeline(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipelines-all'] })
      queryClient.invalidateQueries({ queryKey: ['pipelines'] })
      toast.success(t('messages.updateSuccess'))
      setPipelineDialogOpen(false)
      setEditingPipeline(null)
      setPipelineForm({ pipeline_name: '', pipeline_description: '' })
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('messages.updateError'))),
  })

  const deletePipelineMutation = useMutation({
    mutationFn: (id: string) => pipelineApi.deletePipeline(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipelines-all'] })
      queryClient.invalidateQueries({ queryKey: ['pipelines'] })
      toast.success(t('messages.deleteSuccess'))
      setDeleteDialogOpen(false)
      setItemToDelete(null)
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('messages.deleteError'))),
  })

  const setDefaultMutation = useMutation({
    mutationFn: (id: string) => pipelineApi.setDefaultPipeline(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipelines-all'] })
      queryClient.invalidateQueries({ queryKey: ['pipelines'] })
      toast.success(t('messages.updateSuccess'))
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('messages.updateError'))),
  })

  // Stage mutations
  const createStageMutation = useMutation({
    mutationFn: ({ pipelineId, data }: { pipelineId: string; data: CreatePipelineStageRequest }) =>
      pipelineApi.createStage(pipelineId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipelines-all'] })
      queryClient.invalidateQueries({ queryKey: ['pipeline-stages'] })
      toast.success(t('messages.createSuccess'))
      setStageDialogOpen(false)
      setStageForm({ stage_name: '', stage_color: '#3B82F6', probability: 0 })
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('messages.createError'))),
  })

  const updateStageMutation = useMutation({
    mutationFn: ({ pipelineId, stageId, data }: { pipelineId: string; stageId: string; data: CreatePipelineStageRequest }) =>
      pipelineApi.updateStage(pipelineId, stageId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipelines-all'] })
      queryClient.invalidateQueries({ queryKey: ['pipeline-stages'] })
      toast.success(t('messages.updateSuccess'))
      setStageDialogOpen(false)
      setEditingStage(null)
      setStageForm({ stage_name: '', stage_color: '#3B82F6', probability: 0 })
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('messages.updateError'))),
  })

  const deleteStageMutation = useMutation({
    mutationFn: ({ pipelineId, stageId }: { pipelineId: string; stageId: string }) =>
      pipelineApi.deleteStage(pipelineId, stageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipelines-all'] })
      queryClient.invalidateQueries({ queryKey: ['pipeline-stages'] })
      toast.success(t('messages.deleteSuccess'))
      setDeleteDialogOpen(false)
      setItemToDelete(null)
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('messages.deleteError'))),
  })

  // Handlers
  const handleEditPipeline = (pipeline: Pipeline) => {
    setEditingPipeline(pipeline)
    setPipelineForm({ pipeline_name: pipeline.pipeline_name, pipeline_description: pipeline.pipeline_description || '' })
    setPipelineDialogOpen(true)
  }

  const handleAddStage = (pipelineId: string) => {
    setSelectedPipelineId(pipelineId)
    setEditingStage(null)
    setStageForm({ stage_name: '', stage_color: '#3B82F6', probability: 0 })
    setStageDialogOpen(true)
  }

  const handleEditStage = (pipelineId: string, stage: PipelineStage) => {
    setSelectedPipelineId(pipelineId)
    setEditingStage(stage)
    setStageForm({
      stage_name: stage.stage_name,
      stage_color: stage.stage_color,
      probability: stage.probability,
      is_won_stage: stage.is_won_stage,
      is_lost_stage: stage.is_lost_stage,
    })
    setStageDialogOpen(true)
  }

  const handleDeletePipeline = (pipeline: Pipeline) => {
    setItemToDelete({ type: 'pipeline', id: pipeline.pipeline_id, name: pipeline.pipeline_name })
    setDeleteDialogOpen(true)
  }

  const handleDeleteStage = (pipelineId: string, stage: PipelineStage) => {
    setItemToDelete({ type: 'stage', id: stage.stage_id, name: stage.stage_name, pipelineId })
    setDeleteDialogOpen(true)
  }

  const handlePipelineSubmit = () => {
    if (editingPipeline) {
      updatePipelineMutation.mutate({ id: editingPipeline.pipeline_id, data: pipelineForm })
    } else {
      createPipelineMutation.mutate(pipelineForm)
    }
  }

  const handleStageSubmit = () => {
    if (!selectedPipelineId) return
    if (editingStage) {
      updateStageMutation.mutate({ pipelineId: selectedPipelineId, stageId: editingStage.stage_id, data: stageForm })
    } else {
      createStageMutation.mutate({ pipelineId: selectedPipelineId, data: stageForm })
    }
  }

  const handleConfirmDelete = () => {
    if (!itemToDelete) return
    if (itemToDelete.type === 'pipeline') {
      deletePipelineMutation.mutate(itemToDelete.id)
    } else if (itemToDelete.pipelineId) {
      deleteStageMutation.mutate({ pipelineId: itemToDelete.pipelineId, stageId: itemToDelete.id })
    }
  }

  if (isLoading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('crm.pipelineSettings', 'Pipeline Settings')}</h1>
          <p className="text-muted-foreground">{t('crm.pipelineSettingsDescription', 'Manage your sales pipelines and stages')}</p>
        </div>
        <Button onClick={() => { setEditingPipeline(null); setPipelineForm({ pipeline_name: '', pipeline_description: '' }); setPipelineDialogOpen(true) }}>
          <Plus className="h-4 w-4 mr-2" />
          {t('crm.addPipeline', 'Add Pipeline')}
        </Button>
      </div>

      {pipelines.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground mb-4">{t('crm.noPipelines', 'No pipelines configured yet')}</p>
            <Button onClick={() => setPipelineDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              {t('crm.createFirstPipeline', 'Create your first pipeline')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {pipelines.map((pipeline) => (
            <PipelineCard
              key={pipeline.pipeline_id}
              pipeline={pipeline}
              t={t}
              onEdit={handleEditPipeline}
              onDelete={handleDeletePipeline}
              onSetDefault={(id) => setDefaultMutation.mutate(id)}
              onAddStage={handleAddStage}
              onEditStage={handleEditStage}
              onDeleteStage={handleDeleteStage}
            />
          ))}
        </div>
      )}

      {/* Pipeline Dialog */}
      <Dialog open={pipelineDialogOpen} onOpenChange={setPipelineDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingPipeline ? t('crm.editPipeline', 'Edit Pipeline') : t('crm.addPipeline', 'Add Pipeline')}</DialogTitle>
            <DialogDescription>{t('crm.pipelineDialogDescription', 'Configure your sales pipeline')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pipeline_name">{t('crm.pipelineName', 'Pipeline Name')}</Label>
              <Input
                id="pipeline_name"
                value={pipelineForm.pipeline_name}
                onChange={(e) => setPipelineForm({ ...pipelineForm, pipeline_name: e.target.value })}
                placeholder={t('crm.pipelineNamePlaceholder', 'e.g., Sales Pipeline')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pipeline_description">{t('crm.description', 'Description')}</Label>
              <Textarea
                id="pipeline_description"
                value={pipelineForm.pipeline_description || ''}
                onChange={(e) => setPipelineForm({ ...pipelineForm, pipeline_description: e.target.value })}
                placeholder={t('crm.pipelineDescriptionPlaceholder', 'Describe this pipeline...')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPipelineDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
            <Button onClick={handlePipelineSubmit} disabled={!pipelineForm.pipeline_name}>
              {editingPipeline ? t('common.save', 'Save') : t('common.create', 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stage Dialog */}
      <Dialog open={stageDialogOpen} onOpenChange={setStageDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingStage ? t('crm.editStage', 'Edit Stage') : t('crm.addStage', 'Add Stage')}</DialogTitle>
            <DialogDescription>{t('crm.stageDialogDescription', 'Configure pipeline stage')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="stage_name">{t('crm.stageName', 'Stage Name')}</Label>
              <Input
                id="stage_name"
                value={stageForm.stage_name}
                onChange={(e) => setStageForm({ ...stageForm, stage_name: e.target.value })}
                placeholder={t('crm.stageNamePlaceholder', 'e.g., Qualification')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('crm.stageColor', 'Stage Color')}</Label>
              <div className="flex gap-2 flex-wrap">
                {DEFAULT_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`w-8 h-8 rounded-full border-2 ${stageForm.stage_color === color ? 'border-primary' : 'border-transparent'}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setStageForm({ ...stageForm, stage_color: color })}
                  />
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="probability">{t('crm.probability', 'Probability')} (%)</Label>
              <Input
                id="probability"
                type="number"
                min={0}
                max={100}
                value={stageForm.probability || 0}
                onChange={(e) => setStageForm({ ...stageForm, probability: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="is_won_stage">{t('crm.wonStage', 'Won Stage')}</Label>
              <Switch
                id="is_won_stage"
                checked={stageForm.is_won_stage || false}
                onCheckedChange={(checked) => setStageForm({ ...stageForm, is_won_stage: checked, is_lost_stage: checked ? false : stageForm.is_lost_stage })}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="is_lost_stage">{t('crm.lostStage', 'Lost Stage')}</Label>
              <Switch
                id="is_lost_stage"
                checked={stageForm.is_lost_stage || false}
                onCheckedChange={(checked) => setStageForm({ ...stageForm, is_lost_stage: checked, is_won_stage: checked ? false : stageForm.is_won_stage })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStageDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
            <Button onClick={handleStageSubmit} disabled={!stageForm.stage_name}>
              {editingStage ? t('common.save', 'Save') : t('common.create', 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('common.confirmDelete', 'Confirm Delete')}</DialogTitle>
            <DialogDescription>
              {t('common.deleteConfirmation', 'Are you sure you want to delete')} "{itemToDelete?.name}"?
              {itemToDelete?.type === 'pipeline' && (
                <span className="block mt-2 text-destructive">
                  {t('crm.deletePipelineWarning', 'This will also delete all stages and may affect existing deals.')}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
            <Button variant="destructive" onClick={handleConfirmDelete}>{t('common.delete', 'Delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

